import React, { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import PageTitle from "../../../layouts/PageTitle";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  middleName: "",
  date_birth: "",
  address: "",
  post_codes: "",
  city: "",
  a_select: "",
};

const INITIAL_FILES = {
  your_image: null,
  nid_front_image: null,
  nid_back_image: null,
  passport_image: null,
  driver_image: null,
};

const INITIAL_PREVIEWS = {
  your_image: "",
  nid_front_image: "",
  nid_back_image: "",
  passport_image: "",
  driver_image: "",
};

/* =========================================================
   KYC - ONE PAGE FORM
========================================================= */

const Wizard = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState(INITIAL_FILES);
  const [previews, setPreviews] = useState(INITIAL_PREVIEWS);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* =========================================================
     CLEAN PREVIEW URLS
  ========================================================= */

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  /* =========================================================
     INPUT
  ========================================================= */

  const handleInput = (e) => {
    const { name, value } = e.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

    // When document type changes, clear old document files.
    if (name === "a_select") {
      setFiles((previous) => ({
        ...previous,
        nid_front_image: null,
        nid_back_image: null,
        passport_image: null,
        driver_image: null,
      }));

      setPreviews((previous) => {
        [
          "nid_front_image",
          "nid_back_image",
          "passport_image",
          "driver_image",
        ].forEach((key) => {
          if (previous[key]) {
            URL.revokeObjectURL(previous[key]);
          }
        });

        return {
          ...previous,
          nid_front_image: "",
          nid_back_image: "",
          passport_image: "",
          driver_image: "",
        };
      });

      setErrors((previous) => ({
        ...previous,
        nid_front_image: "",
        nid_back_image: "",
        passport_image: "",
        driver_image: "",
      }));
    }
  };

  /* =========================================================
     FILE INPUT
  ========================================================= */

  const handleFile = (fieldName) => (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((previous) => ({
        ...previous,
        [fieldName]:
          "Please upload JPG, JPEG, PNG or WEBP image.",
      }));
      e.target.value = "";
      return;
    }

    // 8 MB frontend safety limit.
    if (file.size > 8 * 1024 * 1024) {
      setErrors((previous) => ({
        ...previous,
        [fieldName]:
          "Image size must not exceed 8 MB.",
      }));
      e.target.value = "";
      return;
    }

    setFiles((previous) => ({
      ...previous,
      [fieldName]: file,
    }));

    setPreviews((previous) => {
      if (previous[fieldName]) {
        URL.revokeObjectURL(previous[fieldName]);
      }

      return {
        ...previous,
        [fieldName]: URL.createObjectURL(file),
      };
    });

    setErrors((previous) => ({
      ...previous,
      [fieldName]: "",
    }));
  };

  /* =========================================================
     DOCUMENT INFO
  ========================================================= */

  const documentInfo = useMemo(() => {
    if (form.a_select === "nid") {
      return {
        title: "National ID Card",
        description:
          "Upload clear images of both the front and back side of your NID.",
        icon: "fas fa-id-card",
      };
    }

    if (form.a_select === "passport") {
      return {
        title: "Passport",
        description:
          "Upload a clear image of the information/photo page of your passport.",
        icon: "fas fa-passport",
      };
    }

    if (form.a_select === "driver") {
      return {
        title: "Driver's License",
        description:
          "Upload a clear image of your valid driver's license.",
        icon: "fas fa-address-card",
      };
    }

    return {
      title: "Select an identity document",
      description:
        "Choose the government-issued document you want to use for verification.",
      icon: "fas fa-file-shield",
    };
  }, [form.a_select]);

  /* =========================================================
     FRONTEND VALIDATION
  ========================================================= */

  const validateForm = () => {
    const validation = {};

    if (!form.firstName.trim()) {
      validation.firstName = "First name is required.";
    }

    if (!form.lastName.trim()) {
      validation.lastName = "Last name is required.";
    }

    // The old form marked Middle Name as required, so preserve it.
    if (!form.middleName.trim()) {
      validation.middleName = "Middle name is required.";
    }

    if (!form.date_birth) {
      validation.date_birth = "Date of birth is required.";
    }

    if (!form.address.trim()) {
      validation.address = "Address is required.";
    }

    if (!form.post_codes.trim()) {
      validation.post_codes =
        "Post code / ZIP code is required.";
    }

    if (!form.city.trim()) {
      validation.city = "City is required.";
    }

    if (!files.your_image) {
      validation.your_image =
        "Your recent photo is required.";
    }

    if (!form.a_select) {
      validation.a_select =
        "Please select a government-issued document.";
    }

    if (form.a_select === "nid") {
      if (!files.nid_front_image) {
        validation.nid_front_image =
          "NID front image is required.";
      }

      if (!files.nid_back_image) {
        validation.nid_back_image =
          "NID back image is required.";
      }
    }

    if (
      form.a_select === "passport" &&
      !files.passport_image
    ) {
      validation.passport_image =
        "Passport image is required.";
    }

    if (
      form.a_select === "driver" &&
      !files.driver_image
    ) {
      validation.driver_image =
        "Driver's license image is required.";
    }

    setErrors(validation);

    if (Object.keys(validation).length > 0) {
      const firstInvalid = Object.keys(validation)[0];

      setTimeout(() => {
        document
          .querySelector(
            `[data-field="${firstInvalid}"]`
          )
          ?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
      }, 50);

      return false;
    }

    return true;
  };

  /* =========================================================
     SUBMIT
     Same backend endpoint + same field names as old code.
  ========================================================= */

  const submitProduct = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    if (!validateForm()) {
      swal(
        "Check Required Fields",
        "Please complete all required KYC information before submitting.",
        "warning"
      );
      return;
    }

    const formData = new FormData();

    formData.append("key_s", API_KEY);
    formData.append("firstName", form.firstName.trim());
    formData.append("lastName", form.lastName.trim());
    formData.append("middleName", form.middleName.trim());
    formData.append("date_birth", form.date_birth);
    formData.append("address", form.address.trim());
    formData.append("post_codes", form.post_codes.trim());
    formData.append("city", form.city.trim());
    formData.append("your_image", files.your_image);
    formData.append("a_select", form.a_select);

    // Preserve the original API field names. Send only the
    // document files that are relevant to the selected type.
    if (form.a_select === "nid") {
      formData.append(
        "nid_front_image",
        files.nid_front_image
      );
      formData.append(
        "nid_back_image",
        files.nid_back_image
      );
    }

    if (form.a_select === "passport") {
      formData.append(
        "passport_image",
        files.passport_image
      );
    }

    if (form.a_select === "driver") {
      formData.append(
        "driver_image",
        files.driver_image
      );
    }

    try {
      setLoading(true);
      setErrors({});

      const res = await axios.post(
        "/kyc-verification",
        formData
      );

      if (res?.data?.status === "success") {
        if (res?.data?.accessToken) {
          localStorage.setItem(
            "auth_token",
            res.data.accessToken
          );
        }

        swal(
          "KYC Submitted",
          res?.data?.message ||
            "Your identity verification has been submitted successfully.",
          "success"
        );

        return;
      }

      if (res?.data?.status === "fail") {
        swal(
          "Warning",
          res?.data?.message ||
            "Unable to submit KYC verification.",
          "warning"
        );
        return;
      }

      const validationErrors =
        res?.data?.validation_error || {};

      setErrors(validationErrors);

      if (
        validationErrors &&
        Object.keys(validationErrors).length > 0
      ) {
        swal(
          "Check Required Fields",
          "Please review the highlighted information and submit again.",
          "warning"
        );
      }
    } catch (error) {
      console.error("KYC submit error:", error);

      if (error?.response?.status === 422) {
        const validationErrors =
          error?.response?.data?.errors ||
          error?.response?.data?.validation_error ||
          {};

        setErrors(validationErrors);

        swal(
          "Check Required Fields",
          error?.response?.data?.message ||
            "Please review the highlighted information.",
          "warning"
        );

        return;
      }

      swal(
        "Error",
        error?.response?.data?.message ||
          "Unable to submit KYC verification. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     HELPERS
  ========================================================= */

  const fieldError = (name) => {
    const error = errors?.[name];

    if (Array.isArray(error)) {
      return error[0] || "";
    }

    return error || "";
  };

  const hasError = (name) =>
    Boolean(fieldError(name));

  /* =========================================================
     VIEW
  ========================================================= */

  return (
    <Fragment>
      <KycStyles />

      <div className="kyc-one-page">
        <div className="kyc-wrap">

          {/* =================================================
              FORM
          ================================================== */}

          <form
            onSubmit={submitProduct}
            encType="multipart/form-data"
            noValidate
          >
            {/* ===============================================
                PERSONAL INFORMATION
            ================================================ */}

            <section className="kyc-card">
              <div className="kyc-card-head">
                <div className="kyc-section-icon">
                  <i className="fas fa-user"></i>
                </div>

                <div>
                  <div className="kyc-step-label">
                    SECTION 01
                  </div>

                  <h2>Personal Information</h2>

                  <p>
                    Enter your legal name and date of birth
                    exactly as shown on your identity
                    document.
                  </p>
                </div>
              </div>

              <div className="kyc-grid kyc-grid-2">
                <Field
                  label="First Name"
                  required
                  name="firstName"
                  value={form.firstName}
                  onChange={handleInput}
                  placeholder="Enter first name"
                  icon="fas fa-user"
                  error={fieldError("firstName")}
                />

                <Field
                  label="Last Name"
                  required
                  name="lastName"
                  value={form.lastName}
                  onChange={handleInput}
                  placeholder="Enter last name"
                  icon="fas fa-user"
                  error={fieldError("lastName")}
                />

                <Field
                  label="Middle Name"
                  required
                  name="middleName"
                  value={form.middleName}
                  onChange={handleInput}
                  placeholder="Enter middle name"
                  icon="fas fa-user-tag"
                  error={fieldError("middleName")}
                />

                <Field
                  label="Date of Birth"
                  required
                  name="date_birth"
                  type="date"
                  value={form.date_birth}
                  onChange={handleInput}
                  icon="fas fa-calendar-alt"
                  error={fieldError("date_birth")}
                />
              </div>
            </section>

            {/* ===============================================
                ADDRESS
            ================================================ */}

            <section className="kyc-card">
              <div className="kyc-card-head">
                <div className="kyc-section-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>

                <div>
                  <div className="kyc-step-label">
                    SECTION 02
                  </div>

                  <h2>Address Information</h2>

                  <p>
                    Provide your current residential address
                    and location details.
                  </p>
                </div>
              </div>

              <div className="kyc-grid">
                <div className="kyc-full">
                  <Field
                    label="Your Address"
                    required
                    name="address"
                    value={form.address}
                    onChange={handleInput}
                    placeholder="House, road, area or full address"
                    icon="fas fa-home"
                    error={fieldError("address")}
                  />
                </div>

                <div className="kyc-grid kyc-grid-2 kyc-full">
                  <Field
                    label="Post Code / ZIP Code"
                    required
                    name="post_codes"
                    value={form.post_codes}
                    onChange={handleInput}
                    placeholder="Enter post code"
                    icon="fas fa-map-pin"
                    error={fieldError("post_codes")}
                  />

                  <Field
                    label="City"
                    required
                    name="city"
                    value={form.city}
                    onChange={handleInput}
                    placeholder="Enter city"
                    icon="fas fa-city"
                    error={fieldError("city")}
                  />
                </div>
              </div>
            </section>

            {/* ===============================================
                PHOTO + DOCUMENT
            ================================================ */}

            <section className="kyc-card">
              <div className="kyc-card-head">
                <div className="kyc-section-icon">
                  <i className="fas fa-id-card"></i>
                </div>

                <div>
                  <div className="kyc-step-label">
                    SECTION 03
                  </div>

                  <h2>Identity Documents</h2>

                  <p>
                    Upload a recent photo and one valid
                    government-issued identity document.
                    Make sure all details are clear and
                    readable.
                  </p>
                </div>
              </div>

              {/* YOUR PHOTO */}

              <div
                className="kyc-subsection"
                data-field="your_image"
              >
                <div className="kyc-subsection-head">
                  <div>
                    <h3>
                      Your Recent Photo
                      <span>*</span>
                    </h3>

                    <p>
                      Upload a clear recent photo of yourself.
                    </p>
                  </div>

                  <span className="kyc-file-note">
                    JPG / PNG / WEBP · Max 8 MB
                  </span>
                </div>

                <FileUpload
                  id="your_image"
                  icon="fas fa-camera"
                  title="Upload Your Photo"
                  description="Choose a clear recent image from your device"
                  preview={previews.your_image}
                  previewAlt="Your Photo"
                  onChange={handleFile("your_image")}
                  error={fieldError("your_image")}
                  largePreview
                />
              </div>

              <div className="kyc-divider"></div>

              {/* DOCUMENT TYPE */}

              <div
                className="kyc-subsection"
                data-field="a_select"
              >
                <div className="kyc-subsection-head">
                  <div>
                    <h3>
                      Government-issued Document
                      <span>*</span>
                    </h3>

                    <p>
                      Select the document you want to use for
                      KYC verification.
                    </p>
                  </div>
                </div>

                <div
                  className={`kyc-select-wrap ${
                    hasError("a_select")
                      ? "kyc-control-error"
                      : ""
                  }`}
                >
                  <span className="kyc-control-icon">
                    <i className="fas fa-id-badge"></i>
                  </span>

                  <select
                    name="a_select"
                    value={form.a_select}
                    onChange={handleInput}
                  >
                    <option value="">
                      Select document type
                    </option>
                    <option value="nid">
                      National ID Card
                    </option>
                    <option value="passport">
                      Passport
                    </option>
                    <option value="driver">
                      Driver&apos;s License
                    </option>
                  </select>

                  <span className="kyc-select-arrow">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </div>

                {fieldError("a_select") && (
                  <ErrorText>
                    {fieldError("a_select")}
                  </ErrorText>
                )}
              </div>

              {/* DOCUMENT SELECTION SUMMARY */}

              <div className="kyc-doc-summary">
                <div className="kyc-doc-summary-icon">
                  <i className={documentInfo.icon}></i>
                </div>

                <div>
                  <strong>{documentInfo.title}</strong>
                  <p>{documentInfo.description}</p>
                </div>
              </div>

              {/* NID */}

              {form.a_select === "nid" && (
                <div className="kyc-document-grid">
                  <div data-field="nid_front_image">
                    <FileUpload
                      id="nid_front_image"
                      icon="fas fa-id-card"
                      title="NID Front Side"
                      description="Upload the front side of your National ID"
                      preview={previews.nid_front_image}
                      previewAlt="NID Front"
                      onChange={handleFile(
                        "nid_front_image"
                      )}
                      error={fieldError(
                        "nid_front_image"
                      )}
                    />
                  </div>

                  <div data-field="nid_back_image">
                    <FileUpload
                      id="nid_back_image"
                      icon="fas fa-id-card"
                      title="NID Back Side"
                      description="Upload the back side of your National ID"
                      preview={previews.nid_back_image}
                      previewAlt="NID Back"
                      onChange={handleFile(
                        "nid_back_image"
                      )}
                      error={fieldError(
                        "nid_back_image"
                      )}
                    />
                  </div>
                </div>
              )}

              {/* PASSPORT */}

              {form.a_select === "passport" && (
                <div
                  className="kyc-single-document"
                  data-field="passport_image"
                >
                  <FileUpload
                    id="passport_image"
                    icon="fas fa-passport"
                    title="Passport Information Page"
                    description="Upload the passport page containing your photo and personal information"
                    preview={previews.passport_image}
                    previewAlt="Passport"
                    onChange={handleFile(
                      "passport_image"
                    )}
                    error={fieldError(
                      "passport_image"
                    )}
                  />
                </div>
              )}

              {/* DRIVER */}

              {form.a_select === "driver" && (
                <div
                  className="kyc-single-document"
                  data-field="driver_image"
                >
                  <FileUpload
                    id="driver_image"
                    icon="fas fa-address-card"
                    title="Driver's License"
                    description="Upload a clear image of your driver's license"
                    preview={previews.driver_image}
                    previewAlt="Driver's License"
                    onChange={handleFile(
                      "driver_image"
                    )}
                    error={fieldError(
                      "driver_image"
                    )}
                  />
                </div>
              )}
            </section>

            {/* ===============================================
                REVIEW / SUBMIT
            ================================================ */}

            <section className="kyc-submit-card">
              <div className="kyc-submit-info">
                <div className="kyc-submit-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>

                <div>
                  <h3>Ready to submit your KYC?</h3>

                  <p>
                    Please review all information and images
                    before submission. Make sure your
                    identity document is valid and clearly
                    readable.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                className="kyc-submit-btn"
                disabled={loading}
              >
                <i
                  className={
                    loading
                      ? "fas fa-spinner fa-spin"
                      : "fas fa-paper-plane"
                  }
                ></i>

                {loading
                  ? "Submitting KYC..."
                  : "Submit KYC Verification"}
              </button>
            </section>
          </form>

          <div className="kyc-security-note">
            <i className="fas fa-lock"></i>

            <div>
              <strong>Your documents are sensitive</strong>

              <p>
                Only upload your KYC documents through your
                secure account. Never send your identity
                documents or account password to another
                person.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

/* =========================================================
   TEXT FIELD
========================================================= */

const Field = ({
  label,
  required = false,
  name,
  value,
  onChange,
  placeholder = "",
  icon,
  error,
  type = "text",
}) => (
  <div
    className="kyc-field"
    data-field={name}
  >
    <label htmlFor={`kyc-${name}`}>
      {label}
      {required && <span>*</span>}
    </label>

    <div
      className={`kyc-input-wrap ${
        error ? "kyc-control-error" : ""
      }`}
    >
      <span className="kyc-control-icon">
        <i className={icon}></i>
      </span>

      <input
        id={`kyc-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>

    {error && <ErrorText>{error}</ErrorText>}
  </div>
);

/* =========================================================
   FILE UPLOAD
========================================================= */

const FileUpload = ({
  id,
  icon,
  title,
  description,
  preview,
  previewAlt,
  onChange,
  error,
  largePreview = false,
}) => (
  <div className="kyc-upload-wrap">
    <label
      htmlFor={`kyc-file-${id}`}
      className={`kyc-upload-box ${
        error ? "kyc-upload-error" : ""
      }`}
    >
      {!preview ? (
        <>
          <div className="kyc-upload-icon">
            <i className={icon}></i>
          </div>

          <div className="kyc-upload-copy">
            <strong>{title}</strong>
            <span>{description}</span>
            <small>
              Click here to choose an image
            </small>
          </div>

          <div className="kyc-upload-action">
            <i className="fas fa-cloud-upload-alt"></i>
            Browse
          </div>
        </>
      ) : (
        <div
          className={`kyc-preview ${
            largePreview
              ? "kyc-preview-profile"
              : ""
          }`}
        >
          <img
            src={preview}
            alt={previewAlt}
          />

          <div className="kyc-preview-overlay">
            <span>
              <i className="fas fa-sync-alt"></i>
              Change Image
            </span>
          </div>

          <div className="kyc-preview-check">
            <i className="fas fa-check"></i>
          </div>
        </div>
      )}
    </label>

    <input
      id={`kyc-file-${id}`}
      className="kyc-file-input"
      type="file"
      accept="image/jpeg,image/jpg,image/png,image/webp"
      onChange={onChange}
    />

    {error && <ErrorText>{error}</ErrorText>}
  </div>
);

/* =========================================================
   ERROR
========================================================= */

const ErrorText = ({ children }) => (
  <div className="kyc-error-text">
    <i className="fas fa-exclamation-circle"></i>
    <span>{children}</span>
  </div>
);

/* =========================================================
   STYLES
========================================================= */

const KycStyles = () => (
  <style>{`
    .kyc-one-page {
      --kyc-primary: #635bff;
      --kyc-primary-dark: #4f46e5;
      --kyc-primary-soft: #f2f0ff;
      --kyc-green: #12b76a;
      --kyc-red: #f04438;
      --kyc-text: #182230;
      --kyc-muted: #667085;
      --kyc-border: #e4e7ec;
      --kyc-soft: #f8f9fc;

      min-height: 100vh;
      padding: 20px;
      border-radius: 20px;
      background: #fff;
      color: var(--kyc-text);
      font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
    }

    .kyc-wrap {
      width: 100%;
      max-width: 1180px;
      margin: 0 auto;
    }

    /* =====================================================
       HERO
    ===================================================== */

    .kyc-hero {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 30px;
      position: relative;
      overflow: hidden;
      margin-bottom: 20px;
      padding: 32px;
      border-radius: 22px;
      background:
        radial-gradient(
          circle at 88% 10%,
          rgba(117, 229, 170, 0.16),
          transparent 32%
        ),
        linear-gradient(
          135deg,
          #171b2d 0%,
          #272a4b 58%,
          #383264 100%
        );
      box-shadow: 0 16px 42px rgba(26, 32, 55, 0.12);
    }

    .kyc-hero-main {
      display: flex;
      align-items: center;
      gap: 20px;
      min-width: 0;
    }

    .kyc-hero-icon {
      flex: 0 0 66px;
      width: 66px;
      height: 66px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(255, 255, 255, 0.13);
      border-radius: 19px;
      background: rgba(255, 255, 255, 0.08);
      color: #75e5aa;
      font-size: 28px;
    }

    .kyc-kicker {
      display: block;
      margin-bottom: 7px;
      color: #bbb7ff;
      font-size: 12px;
      font-weight: 850;
      letter-spacing: 0.8px;
    }

    .kyc-hero h1 {
      margin: 0 0 8px;
      color: #fff;
      font-size: 31px;
      line-height: 1.2;
      font-weight: 900;
    }

    .kyc-hero p {
      max-width: 680px;
      margin: 0;
      color: rgba(255, 255, 255, 0.7);
      font-size: 14px;
      line-height: 1.7;
    }

    .kyc-hero-badge {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 14px 16px;
      border: 1px solid rgba(117, 229, 170, 0.18);
      border-radius: 15px;
      background: rgba(117, 229, 170, 0.08);
    }

    .kyc-hero-badge > i {
      color: #75e5aa;
      font-size: 22px;
    }

    .kyc-hero-badge small {
      display: block;
      margin-bottom: 2px;
      color: rgba(255, 255, 255, 0.52);
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.5px;
    }

    .kyc-hero-badge strong {
      color: #fff;
      font-size: 13px;
      font-weight: 800;
    }

    /* =====================================================
       CARDS
    ===================================================== */

    .kyc-card {
      margin-bottom: 18px;
      padding: 25px;
      border: 1px solid var(--kyc-border);
      border-radius: 20px;
      background: #fff;
      box-shadow: 0 7px 25px rgba(27, 36, 61, 0.045);
    }

    .kyc-card-head {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding-bottom: 19px;
      margin-bottom: 21px;
      border-bottom: 1px solid #eef0f3;
    }

    .kyc-section-icon {
      flex: 0 0 47px;
      width: 47px;
      height: 47px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 14px;
      background: var(--kyc-primary-soft);
      color: var(--kyc-primary);
      font-size: 18px;
    }

    .kyc-step-label {
      margin-bottom: 3px;
      color: var(--kyc-primary);
      font-size: 10px;
      font-weight: 900;
      letter-spacing: 0.7px;
    }

    .kyc-card-head h2 {
      margin: 0 0 4px;
      color: var(--kyc-text);
      font-size: 19px;
      line-height: 1.35;
      font-weight: 850;
    }

    .kyc-card-head p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 13px;
      line-height: 1.6;
    }

    /* =====================================================
       GRID
    ===================================================== */

    .kyc-grid {
      display: grid;
      gap: 17px;
    }

    .kyc-grid-2 {
      grid-template-columns:
        repeat(2, minmax(0, 1fr));
    }

    .kyc-full {
      grid-column: 1 / -1;
    }

    /* =====================================================
       INPUT
    ===================================================== */

    .kyc-field label {
      display: block;
      margin-bottom: 7px;
      color: #344054;
      font-size: 13px;
      font-weight: 800;
    }

    .kyc-field label span,
    .kyc-subsection-head h3 span {
      margin-left: 3px;
      color: var(--kyc-red);
    }

    .kyc-input-wrap,
    .kyc-select-wrap {
      display: flex;
      align-items: center;
      width: 100%;
      height: 51px;
      overflow: hidden;
      border: 1.5px solid #dfe3ea;
      border-radius: 12px;
      background: #fff;
      transition: 0.2s ease;
    }

    .kyc-input-wrap:focus-within,
    .kyc-select-wrap:focus-within {
      border-color: var(--kyc-primary);
      box-shadow: 0 0 0 4px rgba(99, 91, 255, 0.08);
    }

    .kyc-control-icon {
      flex: 0 0 49px;
      width: 49px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      border-right: 1px solid #e8eaf0;
      background: #f8f9fb;
      color: var(--kyc-primary);
      font-size: 14px;
    }

    .kyc-input-wrap input,
    .kyc-select-wrap select {
      width: 100%;
      height: 100%;
      padding: 0 13px;
      border: 0;
      outline: 0;
      background: transparent;
      color: var(--kyc-text);
      font-size: 14px;
      font-weight: 600;
    }

    .kyc-input-wrap input::placeholder {
      color: #98a2b3;
      font-weight: 500;
    }

    .kyc-select-wrap {
      position: relative;
      max-width: 680px;
    }

    .kyc-select-wrap select {
      appearance: none;
      padding-right: 45px;
      cursor: pointer;
    }

    .kyc-select-arrow {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #98a2b3;
      pointer-events: none;
      font-size: 11px;
    }

    .kyc-control-error {
      border-color: var(--kyc-red) !important;
      box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.06);
    }

    .kyc-error-text {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      margin-top: 6px;
      color: var(--kyc-red);
      font-size: 12px;
      font-weight: 650;
      line-height: 1.45;
    }

    .kyc-error-text i {
      margin-top: 2px;
    }

    /* =====================================================
       SUBSECTION
    ===================================================== */

    .kyc-subsection {
      scroll-margin-top: 20px;
    }

    .kyc-subsection-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      margin-bottom: 13px;
    }

    .kyc-subsection-head h3 {
      margin: 0 0 3px;
      color: var(--kyc-text);
      font-size: 15px;
      font-weight: 850;
    }

    .kyc-subsection-head p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 12px;
      line-height: 1.55;
    }

    .kyc-file-note {
      flex: 0 0 auto;
      padding: 6px 9px;
      border-radius: 8px;
      background: #f4f5f7;
      color: #667085;
      font-size: 10px;
      font-weight: 750;
    }

    .kyc-divider {
      height: 1px;
      margin: 24px 0;
      background: #eef0f3;
    }

    /* =====================================================
       UPLOAD
    ===================================================== */

    .kyc-upload-wrap {
      width: 100%;
    }

    .kyc-file-input {
      display: none;
    }

    .kyc-upload-box {
      position: relative;
      min-height: 128px;
      display: flex;
      align-items: center;
      gap: 14px;
      overflow: hidden;
      padding: 17px;
      border: 1.5px dashed #cfd4dc;
      border-radius: 15px;
      background: #fbfcfd;
      cursor: pointer;
      transition: 0.2s ease;
    }

    .kyc-upload-box:hover {
      border-color: var(--kyc-primary);
      background: #faf9ff;
    }

    .kyc-upload-error {
      border-color: var(--kyc-red);
      background: #fffafa;
    }

    .kyc-upload-icon {
      flex: 0 0 48px;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background: var(--kyc-primary-soft);
      color: var(--kyc-primary);
      font-size: 19px;
    }

    .kyc-upload-copy {
      flex: 1;
      min-width: 0;
    }

    .kyc-upload-copy strong {
      display: block;
      margin-bottom: 3px;
      color: var(--kyc-text);
      font-size: 14px;
      font-weight: 850;
    }

    .kyc-upload-copy span {
      display: block;
      margin-bottom: 6px;
      color: var(--kyc-muted);
      font-size: 12px;
      line-height: 1.5;
    }

    .kyc-upload-copy small {
      color: var(--kyc-primary);
      font-size: 10px;
      font-weight: 750;
    }

    .kyc-upload-action {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 9px 12px;
      border: 1px solid #dedaff;
      border-radius: 10px;
      background: #f4f2ff;
      color: var(--kyc-primary);
      font-size: 11px;
      font-weight: 800;
    }

    /* =====================================================
       PREVIEW
    ===================================================== */

    .kyc-preview {
      position: relative;
      width: 100%;
      min-height: 210px;
      overflow: hidden;
      border-radius: 12px;
      background: #eef0f3;
    }

    .kyc-preview-profile {
      max-width: 440px;
      min-height: 270px;
    }

    .kyc-preview img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 210px;
      max-height: 330px;
      object-fit: contain;
      background: #f4f5f7;
    }

    .kyc-preview-profile img {
      min-height: 270px;
    }

    .kyc-preview-overlay {
      position: absolute;
      inset: auto 0 0;
      display: flex;
      justify-content: center;
      padding: 13px;
      background: linear-gradient(
        transparent,
        rgba(17, 24, 39, 0.82)
      );
      color: #fff;
      font-size: 11px;
      font-weight: 750;
    }

    .kyc-preview-overlay span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .kyc-preview-check {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 29px;
      height: 29px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid #fff;
      border-radius: 50%;
      background: var(--kyc-green);
      color: #fff;
      font-size: 10px;
      box-shadow: 0 4px 12px rgba(18, 183, 106, 0.24);
    }

    /* =====================================================
       DOCUMENT
    ===================================================== */

    .kyc-doc-summary {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 15px 0 17px;
      padding: 13px;
      border: 1px solid #e4e1ff;
      border-radius: 13px;
      background: #faf9ff;
    }

    .kyc-doc-summary-icon {
      flex: 0 0 41px;
      width: 41px;
      height: 41px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 11px;
      background: #ece9ff;
      color: var(--kyc-primary);
      font-size: 16px;
    }

    .kyc-doc-summary strong {
      display: block;
      margin-bottom: 2px;
      font-size: 13px;
      font-weight: 850;
    }

    .kyc-doc-summary p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 11px;
      line-height: 1.5;
    }

    .kyc-document-grid {
      display: grid;
      grid-template-columns:
        repeat(2, minmax(0, 1fr));
      gap: 15px;
    }

    .kyc-single-document {
      max-width: 760px;
    }

    /* =====================================================
       SUBMIT
    ===================================================== */

    .kyc-submit-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 25px;
      padding: 22px;
      border: 1px solid #dfe2e8;
      border-radius: 19px;
      background:
        linear-gradient(
          135deg,
          #fafbfc,
          #f6f5ff
        );
    }

    .kyc-submit-info {
      display: flex;
      align-items: center;
      gap: 14px;
      max-width: 690px;
    }

    .kyc-submit-icon {
      flex: 0 0 48px;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 14px;
      background: #ecfdf3;
      color: var(--kyc-green);
      font-size: 19px;
    }

    .kyc-submit-info h3 {
      margin: 0 0 4px;
      color: var(--kyc-text);
      font-size: 16px;
      font-weight: 850;
    }

    .kyc-submit-info p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 12px;
      line-height: 1.6;
    }

    .kyc-submit-btn {
      flex: 0 0 auto;
      min-width: 215px;
      min-height: 51px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 0 19px;
      border: 0;
      border-radius: 13px;
      background: linear-gradient(
        135deg,
        var(--kyc-primary),
        var(--kyc-primary-dark)
      );
      color: #fff;
      font-size: 13px;
      font-weight: 850;
      cursor: pointer;
      box-shadow: 0 10px 22px rgba(99, 91, 255, 0.2);
      transition: 0.2s ease;
    }

    .kyc-submit-btn:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 13px 27px rgba(99, 91, 255, 0.25);
    }

    .kyc-submit-btn:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    /* =====================================================
       SECURITY NOTE
    ===================================================== */

    .kyc-security-note {
      display: flex;
      align-items: flex-start;
      gap: 11px;
      margin-top: 16px;
      padding: 14px 16px;
      border: 1px solid #dcebe3;
      border-radius: 14px;
      background: #f6fbf8;
    }

    .kyc-security-note > i {
      margin-top: 2px;
      color: var(--kyc-green);
      font-size: 15px;
    }

    .kyc-security-note strong {
      display: block;
      margin-bottom: 2px;
      color: var(--kyc-text);
      font-size: 12px;
      font-weight: 850;
    }

    .kyc-security-note p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 11px;
      line-height: 1.6;
    }

    /* =====================================================
       RESPONSIVE
    ===================================================== */

    @media (max-width: 900px) {
      .kyc-hero {
        align-items: flex-start;
        flex-direction: column;
      }

      .kyc-hero-badge {
        width: 100%;
      }

      .kyc-submit-card {
        align-items: stretch;
        flex-direction: column;
      }

      .kyc-submit-btn {
        width: 100%;
      }
    }

    @media (max-width: 700px) {
      .kyc-one-page {
        padding: 10px;
        border-radius: 15px;
      }

      .kyc-hero {
        padding: 22px 17px;
        border-radius: 18px;
      }

      .kyc-hero-main {
        align-items: flex-start;
        flex-direction: column;
      }

      .kyc-hero-icon {
        flex-basis: 55px;
        width: 55px;
        height: 55px;
        border-radius: 16px;
        font-size: 23px;
      }

      .kyc-hero h1 {
        font-size: 26px;
      }

      .kyc-card {
        padding: 18px;
        border-radius: 17px;
      }

      .kyc-grid-2,
      .kyc-document-grid {
        grid-template-columns: 1fr;
      }

      .kyc-card-head {
        align-items: flex-start;
      }

      .kyc-subsection-head {
        flex-direction: column;
        gap: 8px;
      }

      .kyc-upload-box {
        align-items: flex-start;
        flex-direction: column;
      }

      .kyc-upload-action {
        width: 100%;
        justify-content: center;
      }

      .kyc-preview-profile {
        max-width: 100%;
      }
    }

    @media (max-width: 420px) {
      .kyc-one-page {
        padding: 7px;
      }

      .kyc-hero {
        padding: 19px 14px;
      }

      .kyc-hero h1 {
        font-size: 23px;
      }

      .kyc-card {
        padding: 15px;
      }

      .kyc-card-head {
        gap: 10px;
      }

      .kyc-section-icon {
        flex-basis: 40px;
        width: 40px;
        height: 40px;
      }

      .kyc-card-head h2 {
        font-size: 17px;
      }

      .kyc-submit-info {
        align-items: flex-start;
        flex-direction: column;
      }
    }
  `}</style>
);

export default Wizard;
